import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MenuRoutes from "./config";
import { useLang } from "../../context/LanguageContext";

const DRAWER_WIDTH = 250;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
    ({ theme, open }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: open ? 0 : -DRAWER_WIDTH,
        overflowX: "hidden",
    }),
);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        marginLeft: DRAWER_WIDTH,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
}));

const NavGroup = ({ groupKey, items, userTypeId, navigate, t }) => {
    const [expanded, setExpanded] = React.useState(true);
    const visibleItems = items.filter((item) =>
        item.allowedUsers.includes(userTypeId),
    );

    if (visibleItems.length === 0) return null;

    return (
        <>
            <ListItemButton
                onClick={() => setExpanded((prev) => !prev)}
                sx={{ py: 0.5 }}
            >
                <ListItemText
                    primary={t(groupKey)}
                    primaryTypographyProps={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                    }}
                />
                {expanded ? (
                    <ExpandLess fontSize="small" />
                ) : (
                    <ExpandMore fontSize="small" />
                )}
            </ListItemButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                    {visibleItems.map((item) => (
                        <ListItem
                            key={item.route}
                            disablePadding
                            onClick={() => navigate(item.route)}
                        >
                            <ListItemButton sx={{ pl: 3 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(item.textKey)}
                                    primaryTypographyProps={{ fontSize: 14 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Collapse>
            <Divider />
        </>
    );
};

const AppContainer = ({ children }) => {
    const signOut = useSignOut();
    const navigate = useNavigate();
    const userData = useAuthUser();
    const theme = useTheme();
    const [open, setOpen] = React.useState(true);
    const { lang, setLang, t } = useLang();

    const userTypeId = userData?.employeeType?.id;

    const handleLangToggle = () => {
        setLang(lang === "ar" ? "en" : "ar");
    };

    const AppDrawer = () => (
        <Drawer
            dir={theme.direction}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
            }}
            variant="persistent"
            anchor="left"
            open={open}
        >
            <DrawerHeader dir={theme.direction}>
                <IconButton onClick={() => setOpen(false)}>
                    {theme.direction === "rtl" ? (
                        <ChevronRightIcon />
                    ) : (
                        <ChevronLeftIcon />
                    )}
                </IconButton>
            </DrawerHeader>
            <Divider />
            <List sx={{ pt: 0 }}>
                {MenuRoutes.map(({ groupKey, items }) => (
                    <NavGroup
                        key={groupKey}
                        groupKey={groupKey}
                        items={items}
                        userTypeId={userTypeId}
                        navigate={navigate}
                        t={t}
                    />
                ))}
            </List>
            <ListItem sx={{ mt: "auto" }}>
                <ListItemButton
                    onClick={() => {
                        signOut();
                        window.location.reload();
                    }}
                    sx={{
                        background: "#f44336",
                        borderRadius: "5px",
                        color: "white",
                        "&:hover": { background: "#f55" },
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("LOGOUT")} />
                </ListItemButton>
            </ListItem>
        </Drawer>
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh" }} dir={theme.direction}>
            <CssBaseline />
            <AppDrawer />
            <AppBar position="fixed" open={open} dir={theme.direction}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={() => setOpen(true)}
                        sx={{ ...(open && { display: "none" }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        sx={{ flexGrow: 1 }}
                        component="div"
                        marginLeft="20px"
                    >
                        {t("APP_TITLE")}
                    </Typography>

                    {/* Language Toggle — top-left (end of toolbar in RTL = visual left) */}
                    <Chip
                        label={lang === "ar" ? "EN" : "ع"}
                        onClick={handleLangToggle}
                        size="small"
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            color: "white",
                            fontWeight: 700,
                            fontSize: 13,
                            letterSpacing: 0.5,
                            cursor: "pointer",
                            border: "1.5px solid rgba(255,255,255,0.4)",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,0.30)",
                            },
                            "& .MuiChip-label": { px: 1.5 },
                        }}
                    />
                </Toolbar>
            </AppBar>
            <Main open={open}>
                <DrawerHeader />
                {children}
            </Main>
            <ToastContainer />
        </Box>
    );
};

export default AppContainer;
